import { readonly, ref, type Ref } from 'vue'
import RecordeApi from '~~/package/apis/recorde/RecordeApi'
import type PreSignedUrlParamsRequest from '~~/package/apis/recorde/request/PreSignedUrlParamsRequest'

// TSK-11198 웹캠 캡처 — 응시(문서)당 녹화기 하나.
//
// 응시 화면은 문항을 옮길 때마다 다시 마운트된다(Nuxt 기본 page key에 taskId가 들어간다). 이 서비스가
// 페이지 인스턴스마다 만들어지던 때는 문항 이동마다 getUserMedia + VP9 MediaRecorder가 하나씩 늘었고,
// 멈추는 곳은 실제 이탈(onBeforeRouteLeave)뿐이라 이전 것들은 응시 내내 인코딩을 계속했다. stage 실측
// (2026-10-13, 평가 24670): HW 인코딩이 없는 PC에서 이동 14회 후 웹뷰가 PC 전체 CPU의 73~76%를 11분간
// 유지했고, 업로드되는 webcam-recording.webm은 마지막 문항 구간뿐이었다(integrity-analyzer는 이를
// Proctive HLS cam보다 우선 사용한다). 그래서 녹화 상태는 모듈 스코프에 하나만 둔다 — useAssessmentSeat과
// 같은 방식.
//
// INVARIANT: start/stop은 클라이언트 라이프사이클(onMounted 이후)에서만 부른다. 모듈 스코프 상태는 SSR에서
// 요청 간에 공유된다.

const FILE_NAME = 'webcam-recording.webm'
const STOP_EVENT_TIMEOUT_MS = 2_000

interface Capture {
  stream: MediaStream
  recorder: MediaRecorder
  chunks: Blob[]
  params: PreSignedUrlParamsRequest | null
}

const stopTracks = (stream: MediaStream | null) => stream?.getTracks().forEach((t) => t.stop())

export interface WebcamRecordingService {
  /** 같은 응시(env/domain/domain_id/user_uuid)의 녹화가 돌거나 시작 중이면 새로 열지 않고 그것을 공유한다. */
  start: (params?: PreSignedUrlParamsRequest) => Promise<boolean>
  /** 녹화를 끝내고 카메라를 놓은 뒤 webcam-recording.webm을 올린다. 중복 호출은 같은 작업을 돌려준다. */
  stop: () => Promise<void>
  isRecording: Readonly<Ref<boolean>>
}

export const createWebcamRecordingService = (): WebcamRecordingService => {
  const isRecording = ref(false)
  let capture: Capture | null = null
  let sessionKey: string | null = null
  let lastParams: PreSignedUrlParamsRequest | null = null
  let starting: Promise<boolean> | null = null
  let lastStop: Promise<void> = Promise.resolve()
  // 직전 녹화의 카메라 해제. 새 녹화는 이걸 기다린 뒤 카메라를 연다 — 같은 장치를 겹쳐 열지 않는다.
  let lastReleased: Promise<void> = Promise.resolve()
  // stop/세션 교체마다 증가한다. getUserMedia가 끝났을 때 값이 바뀌어 있으면 그 사이 녹화가 취소된 것이므로
  // 받은 스트림을 바로 놓는다(응시를 끝냈는데 카메라가 다시 켜지는 것을 막는다).
  let generation = 0

  const upload = async (blob: Blob, params: PreSignedUrlParamsRequest) => {
    try {
      const api = new RecordeApi()
      const url = await api.getPreSignedUrl({ ...params, file_name: FILE_NAME })
      await api.uploadVideo(url, blob)
    } catch (e) {
      console.warn('webcam upload failed', e instanceof Error ? e.message : String(e))
    }
  }

  // 레코더를 멈추고 stop 이벤트(그 전에 오는 마지막 dataavailable 포함)를 받은 뒤 카메라를 놓는다. 오류로 멈춘
  // 레코더도 오류 뒤에 남은 데이터와 stop 이벤트를 보낸다. stop 이벤트가 끝내 오지 않는 경우를 위해 상한을 둔다.
  const release = (c: Capture): Promise<Blob> => new Promise<Blob>((resolve) => {
    const done = () => {
      clearTimeout(timer)
      stopTracks(c.stream)
      resolve(new Blob(c.chunks, { type: c.recorder.mimeType }))
    }
    const timer = setTimeout(done, STOP_EVENT_TIMEOUT_MS)
    c.recorder.onstop = done
    if (c.recorder.state !== 'inactive') c.recorder.stop()
  })

  const stop = (): Promise<void> => {
    generation += 1
    starting = null
    const c = capture
    if (!c) return lastStop
    capture = null
    isRecording.value = false
    const recorded = release(c)
    lastReleased = recorded.then(() => {})
    lastStop = recorded.then(async (blob) => {
      if (c.params && blob.size > 0) await upload(blob, c.params)
    })
    return lastStop
  }

  const begin = async (params: PreSignedUrlParamsRequest | null, gen: number): Promise<boolean> => {
    if (gen !== generation) return false
    let stream: MediaStream | null = null
    try {
      stream = await navigator.mediaDevices.getUserMedia({
        video: { width: 640, height: 480 }, audio: false,
      })
      if (gen !== generation) {
        stopTracks(stream)
        return false
      }
      const mimeType = MediaRecorder.isTypeSupported('video/webm;codecs=vp9')
        ? 'video/webm;codecs=vp9' : 'video/webm'
      const c: Capture = { stream, recorder: new MediaRecorder(stream, { mimeType }), chunks: [], params }
      c.recorder.ondataavailable = (e) => { if (e.data.size > 0) c.chunks.push(e.data) }
      // 녹화 도중 오류가 나도 그때까지 모은 영상은 올린다 — 응시 전체 녹화를 하나로 유지하므로 버리면 다 잃는다.
      c.recorder.onerror = () => { if (capture === c) void stop() }
      c.recorder.start(1000)
      capture = c
      isRecording.value = true
      return true
    } catch {
      // 권한 거부/장치 없음/레코더 초기화 실패 — face 시그널은 중립 처리(잡에서 파일 부재로 인지)
      stopTracks(stream)
      return false
    }
  }

  /**
   * 녹화를 시작한다. 같은 응시의 녹화가 이미 돌거나 시작 중이면 새로 열지 않고 그것을 돌려준다 —
   * 응시 화면은 문항마다 다시 마운트되고, 한 번 진입에 두 번 마운트되기도 한다.
   */
  const start = (params?: PreSignedUrlParamsRequest): Promise<boolean> => {
    const next = params ?? lastParams
    // 같은 응시자·평가면 같은 녹화를 이어 쓴다. 다르면(다른 평가로 이동 등) 이전 녹화를 끝내고 새로 시작한다.
    const key = next ? [next.env, next.domain, next.domain_id, next.user_uuid].join('/') : null
    if (key === sessionKey) {
      if (starting) return starting
      if (capture) return Promise.resolve(true)
    } else if (starting || capture) {
      void stop() // 다른 응시 — 이전 녹화는 마저 올리되, 새 녹화는 카메라 해제만 기다리고 업로드는 기다리지 않는다
    }
    sessionKey = key
    lastParams = next
    generation += 1
    const gen = generation
    const pending = lastReleased.then(() => begin(next, gen))
    starting = pending
    void pending.then(() => { if (starting === pending) starting = null })
    return pending
  }

  return { start, stop, isRecording: readonly(isRecording) }
}

let shared: WebcamRecordingService | null = null

export const useWebcamRecordingService = (): WebcamRecordingService => {
  shared ??= createWebcamRecordingService()
  return shared
}
